import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, where, orderBy, getDocs, doc, writeBatch, getDoc } from 'firebase/firestore';
import { Logo } from '../components/Logo';

function RunePrinter() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userData, logout } = useAuth();
  const [books, setBooks] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [labelSettings, setLabelSettings] = useState({
    barcodeStartPosition: 1,
    spineStartPosition: 1,
    oversizedStartPosition: 1,
    printBarcodes: true,
    printSpines: true
  });
  const [previewMode, setPreviewMode] = useState('barcode');
  // NEW: How many standalone oversized stickers to print
  const [standaloneOversizedCount, setStandaloneOversizedCount] = useState(10);
  const [isTemporaryMode, setIsTemporaryMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Check if we have temporary books from Draupnir
    if (location.state?.temporaryBooks && location.state?.source === 'draupnir') {
      setIsTemporaryMode(true);
      setBooks(location.state.temporaryBooks);
      setSelectedBooks(location.state.temporaryBooks.map((_, index) => index));
      setLoading(false);
    } else {
      loadBooksForPrinting();
    }
    loadPrinterSettings();
  }, [currentUser, location.state]);

  const loadPrinterSettings = async () => {
    if (!currentUser) return;

    try {
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
      const settingsDoc = await getDoc(settingsRef);
      
      if (settingsDoc.exists()) {
        setSettings(settingsDoc.data());
      } else {
        setSettings({
          libraryName: userData?.libraryName || userData?.schoolName || 'School Library',
          schoolName: userData?.schoolName || 'School Library'
        });
      }
    } catch (error) {
      console.error('Error loading printer settings:', error);
      setSettings({
        libraryName: userData?.libraryName || userData?.schoolName || 'School Library',
        schoolName: userData?.schoolName || 'School Library'
      });
    }
  };

  const loadBooksForPrinting = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError('');
    
    try {
      console.log('Loading books for user:', currentUser.uid);
      
      const booksRef = collection(db, 'users', currentUser.uid, 'books');
      
      // Get all books - we'll sort by barcode in memory
      const allBooksQuery = query(booksRef);
      const allSnapshot = await getDocs(allBooksQuery);
      
      console.log('Total books found:', allSnapshot.size);
      
      if (allSnapshot.size === 0) {
        setError('No books found in your collection. Visit the Forge to catalog some books first!');
        setBooks([]);
        setLoading(false);
        return;
      }
      
      const allBooks = allSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Book data:', data);
        return {
          id: doc.id,
          ...data
        };
      });
      
      const printableBooks = allBooks.filter(book => 
        !book.status || 
        book.status === 'pending' || 
        book.status === 'cataloged' ||
        !book.printedAt
      );
      
      // Sort by barcode number (numeric sort)
      printableBooks.sort((a, b) => {
        const barcodeA = parseInt(a.barcode) || 0;
        const barcodeB = parseInt(b.barcode) || 0;
        return barcodeA - barcodeB;
      });
      
      console.log('Printable books sorted by barcode:', printableBooks);
      
      if (printableBooks.length === 0) {
        setError('All books have already been printed! Visit the Forge to catalog new books.');
      }
      
      setBooks(printableBooks);
      setSelectedBooks(printableBooks.map(book => book.id));
      
    } catch (error) {
      console.error('Error loading books:', error);
      setError('Error loading books: ' + error.message);
    }
    setLoading(false);
  };

  const toggleBookSelection = (bookId) => {
    setSelectedBooks(prev => 
      prev.includes(bookId) 
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  };

  const getSelectedBooksData = () => {
    // Get selected books and maintain barcode sort order
    const selectedBooksData = books.filter(book => selectedBooks.includes(book.id));
    // Already sorted by barcode from loadBooksForPrinting, but ensure it stays sorted
    return selectedBooksData.sort((a, b) => {
      const barcodeA = parseInt(a.barcode) || 0;
      const barcodeB = parseInt(b.barcode) || 0;
      return barcodeA - barcodeB;
    });
  };

  // Count oversized books in selection
  const getOversizedCount = () => {
    return getSelectedBooksData().filter(book => book.isOversized).length;
  };

  // Calculate number of sheets needed
  const calculateSheets = () => {
    const selectedBooksData = getSelectedBooksData();
    
    if (previewMode === 'barcode') {
      const labelsPerSheet = 30;
      const startPosition = labelSettings.barcodeStartPosition;
      const firstSheetCapacity = labelsPerSheet - startPosition + 1;
      
      if (selectedBooksData.length <= firstSheetCapacity) {
        return 1;
      }
      const remainingBooks = selectedBooksData.length - firstSheetCapacity;
      return 1 + Math.ceil(remainingBooks / labelsPerSheet);
    } else if (previewMode === 'spine') {
      // For spine labels, we need to account for oversized books getting 2 labels each
      const labelsPerSheet = 50;
      const startPosition = labelSettings.spineStartPosition;
      const oversizedCount = getOversizedCount();
      const totalLabels = selectedBooksData.length + oversizedCount; // Extra label per oversized book
      
      const firstSheetCapacity = labelsPerSheet - startPosition + 1;
      
      if (totalLabels <= firstSheetCapacity) {
        return 1;
      }
      const remainingLabels = totalLabels - firstSheetCapacity;
      return 1 + Math.ceil(remainingLabels / labelsPerSheet);
    } else if (previewMode === 'oversized') {
      // Standalone oversized stickers - 25 per sheet (alternating rows)
      const labelsPerSheet = 25;
      const startPosition = labelSettings.oversizedStartPosition;
      const firstSheetCapacity = labelsPerSheet - startPosition + 1;
      
      if (standaloneOversizedCount <= firstSheetCapacity) {
        return 1;
      }
      const remainingLabels = standaloneOversizedCount - firstSheetCapacity;
      return 1 + Math.ceil(remainingLabels / labelsPerSheet);
    }
    
    return 1;
  };

  // Generate Code 39 barcode SVG - LARGER for better scanning
  const generateBarcodeDataURL = (text) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const code39 = {
      '0': '101001101101', '1': '110100101011', '2': '101100101011', 
      '3': '110110010101', '4': '101001101011', '5': '110100110101', 
      '6': '101100110101', '7': '101001011011', '8': '110100101101', 
      '9': '101100101101'
    };
    
    const startStop = '100101101101';
    let binaryString = startStop;
    
    for (let char of text) {
      if (code39[char]) {
        binaryString += '0' + code39[char];
      }
    }
    binaryString += '0' + startStop;
    
    const barWidth = 3;
    const barHeight = 50;
    canvas.width = binaryString.length * barWidth;
    canvas.height = barHeight;
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'black';
    
    for (let i = 0; i < binaryString.length; i++) {
      if (binaryString[i] === '1') {
        ctx.fillRect(i * barWidth, 0, barWidth, barHeight);
      }
    }
    
    return canvas.toDataURL();
  };

  const truncateTitle = (title, maxLength = 25) => {
    return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
  };

  const getLabelPosition = (index, startPosition, labelsPerRow, labelWidth, labelHeight, marginLeft = 0.5, marginTop = 0.5) => {
    const adjustedIndex = index + startPosition - 1;
    const row = Math.floor(adjustedIndex / labelsPerRow);
    const col = adjustedIndex % labelsPerRow;
    
    return {
      left: marginLeft + (col * labelWidth),
      top: marginTop + (row * labelHeight)
    };
  };

  // Avery 5160 Barcode Label - NO BORDERS like real template
  const BarcodeLabel = ({ book, style }) => {
    const libraryName = settings?.libraryName || settings?.schoolName || userData?.libraryName || userData?.schoolName || 'SCHOOL LIBRARY';
    
    return (
      <div 
        className="absolute bg-white flex flex-col justify-center items-center text-center"
        style={{
          ...style,
          width: '2.625in',
          height: '1in',
          padding: '0.08in',
          fontSize: '7px',
          lineHeight: '1.1',
          pageBreakInside: 'avoid',
          fontFamily: 'Calibri, Arial, sans-serif'
        }}
      >
        <div className="font-bold text-[7px] mb-1">{libraryName.toUpperCase()}</div>
        <img 
          src={generateBarcodeDataURL(book.barcode)} 
          alt={`Barcode ${book.barcode}`}
          className="mb-1"
          style={{ width: '180px', height: '50px' }}
        />
        <div className="font-bold text-[9px] mb-1">{book.barcode}</div>
        <div className="text-[9px] font-medium leading-tight">
          {truncateTitle(book.title, 35)}
        </div>
      </div>
    );
  };

  // Demco Spine Label - Now WITHOUT OVERSIZED prefix (actual category only)
  const SpineLabel = ({ book, style }) => {
    const parseCallNumber = (callNumber) => {
      if (!callNumber) return { line1: '', line2: '', line3: '', line4: '' };
      
      const parts = callNumber.trim().split(/\s+/);
      
      // REMOVED: No longer check for OVERSIZED prefix in call number
      // Oversized books now have their actual category call number
      // The OVERSIZED sticker is printed separately
      
      let workingParts = [...parts];
      
      // Find MS in the parts (case-insensitive)
      const msIndex = workingParts.findIndex(part => part.toUpperCase() === 'MS');
      
      let result = { line1: '', line2: '', line3: '', line4: '' };
      
      if (msIndex !== -1) {
        // MS found - check if it's "MS GN" or "MS FIC" pattern
        const nextPart = workingParts[msIndex + 1]?.toUpperCase();

        if (nextPart === 'GN') {
          // MS GN pattern (MS Graphic Novels or MS GN Non-Fiction)
          // Format: MS / GN / author OR MS / GN / dewey / author
          const remainingParts = workingParts.slice(msIndex + 2);
          if (remainingParts.length === 0) {
            result = { line1: 'MS', line2: 'GN', line3: '', line4: '' };
          } else if (remainingParts.length === 1) {
            // MS GN author (MS Graphic Novels fiction)
            result = { line1: 'MS', line2: 'GN', line3: remainingParts[0], line4: '' };
          } else {
            // MS GN dewey author (MS GN Non-Fiction)
            const authorLetters = remainingParts[remainingParts.length - 1];
            const dewey = remainingParts.slice(0, -1).join(' ');
            result = { line1: 'MS', line2: 'GN', line3: dewey, line4: authorLetters };
          }
        } else if (nextPart === 'FIC') {
          // MS FIC pattern: MS / FIC / author
          const authorLetters = workingParts[msIndex + 2] || '';
          result = { line1: 'MS', line2: 'FIC', line3: authorLetters, line4: '' };
        } else {
          // MS Non-Fiction pattern: MS / dewey / author
          const otherParts = workingParts.slice(msIndex + 1);
          if (otherParts.length === 0) {
            result = { line1: 'MS', line2: '', line3: '', line4: '' };
          } else if (otherParts.length === 1) {
            result = { line1: 'MS', line2: otherParts[0], line3: '', line4: '' };
          } else {
            const authorLetters = otherParts[otherParts.length - 1];
            const dewey = otherParts.slice(0, -1).join(' ');
            result = { line1: 'MS', line2: dewey, line3: authorLetters, line4: '' };
          }
        }
      } else if (workingParts[0]?.toUpperCase() === 'REF') {
        // Reference: REF dewey author
        const remainingParts = workingParts.slice(1);
        if (remainingParts.length === 0) {
          result = { line1: 'REF', line2: '', line3: '', line4: '' };
        } else if (remainingParts.length === 1) {
          result = { line1: 'REF', line2: remainingParts[0], line3: '', line4: '' };
        } else {
          const authorLetters = remainingParts[remainingParts.length - 1];
          const dewey = remainingParts.slice(0, -1).join(' ');
          result = { line1: 'REF', line2: dewey, line3: authorLetters, line4: '' };
        }
      } else if (workingParts[0]?.toUpperCase() === 'E') {
        // Easy or Early Non-Fiction: E author OR E dewey author
        const remainingParts = workingParts.slice(1);
        if (remainingParts.length === 0) {
          result = { line1: 'E', line2: '', line3: '', line4: '' };
        } else if (remainingParts.length === 1) {
          // Just E and author (Easy)
          result = { line1: 'E', line2: remainingParts[0], line3: '', line4: '' };
        } else {
          // E with dewey and author (Early Non-Fiction)
          const authorLetters = remainingParts[remainingParts.length - 1];
          const dewey = remainingParts.slice(0, -1).join(' ');
          result = { line1: 'E', line2: dewey, line3: authorLetters, line4: '' };
        }
      } else {
        // Standard patterns: FIC author, GN author, SPA author, dewey author, Q author
        // Format: classification / author (2 lines) OR classification / dewey / author (3 lines)
        if (workingParts.length === 1) {
          result = { line1: workingParts[0], line2: '', line3: '', line4: '' };
        } else if (workingParts.length === 2) {
          // Two parts: classification / author (e.g., FIC / SMI, GN / SMI, 741.5 / SMI)
          result = { line1: workingParts[0], line2: workingParts[1], line3: '', line4: '' };
        } else {
          // Three+ parts: first part / middle parts joined / last part (author)
          // e.g., GN 741.5 SMI → GN / 741.5 / SMI
          const authorLetters = workingParts[workingParts.length - 1];
          const middleParts = workingParts.slice(1, -1).join(' ');
          result = { line1: workingParts[0], line2: middleParts, line3: authorLetters, line4: '' };
        }
      }
      
      return result;
    };
    
    const { line1, line2, line3, line4 } = parseCallNumber(book.callNumber);

    // Use smaller font and tighter spacing for 4-line labels
    const hasFourLines = line1 && line2 && line3 && line4;
    const fontSize = hasFourLines ? '11px' : '13px';
    const lineSpacing = hasFourLines ? 'mt-0' : 'mt-0.5';

    return (
      <div
        className="absolute bg-white flex flex-col justify-center items-center text-center"
        style={{
          ...style,
          width: '1in',
          height: '1.5in',
          padding: '0.1in',
          fontSize: fontSize,
          lineHeight: '1.0',
          pageBreakInside: 'avoid',
          fontFamily: 'Calibri, Arial, sans-serif'
        }}
      >
        {line1 && (
          <div className="font-bold text-center leading-tight" style={{ fontSize }}>
            {line1}
          </div>
        )}
        {line2 && (
          <div className={`font-bold text-center leading-tight ${lineSpacing}`} style={{ fontSize }}>
            {line2}
          </div>
        )}
        {line3 && (
          <div className={`font-bold text-center leading-tight ${lineSpacing}`} style={{ fontSize }}>
            {line3}
          </div>
        )}
        {line4 && (
          <div className={`font-bold text-center leading-tight ${lineSpacing}`} style={{ fontSize }}>
            {line4}
          </div>
        )}
      </div>
    );
  };

  // NEW: Oversized Sticker - Rotated 90° counter-clockwise
  // Same size as spine labels (1" x 1.5")
  const OversizedLabel = ({ style }) => {
    return (
      <div 
        style={{
          ...style,
          position: 'absolute',
          width: '1in',
          height: '1.5in',
          pageBreakInside: 'avoid',
          fontFamily: 'Calibri, Arial, sans-serif',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'white'
        }}
      >
        <span
          style={{
            transform: 'rotate(-90deg)',
            fontWeight: 'bold',
            fontSize: '14px',
            letterSpacing: '0.5px',
            whiteSpace: 'nowrap'
          }}
        >
          OVERSIZED
        </span>
      </div>
    );
  };

  // Single Sheet Component
  const PreviewSheet = ({ books, sheetNumber, isFirstSheet, labelItems = null }) => {
    if (previewMode === 'barcode') {
      // Avery 5160: 3 columns x 10 rows = 30 labels
      return (
        <div 
          className="relative bg-white mx-auto print:border-none" 
          style={{ 
            width: '8.5in', 
            height: '11in',
            pageBreakAfter: 'always'
          }}
        >
          <div className="text-xs text-slate-600 p-2 print:hidden">
            Avery 5160 - Barcode Labels (Sheet {sheetNumber}) - 3 columns × 10 rows
          </div>
          {books.map((book, index) => {
            const startPosition = isFirstSheet ? labelSettings.barcodeStartPosition : 1;
            const position = getLabelPosition(
              index, 
              startPosition, 
              3,
              2.75,
              1,
              0.21875,
              0.5
            );
            
            return (
              <BarcodeLabel 
                key={book.id} 
                book={book} 
                style={{ 
                  left: `${position.left}in`, 
                  top: `${position.top}in` 
                }} 
              />
            );
          })}
        </div>
      );
    } else if (previewMode === 'spine') {
      // Demco: 5 columns x 10 rows = 50 labels
      // Now uses labelItems which includes both spine labels and oversized stickers
      const items = labelItems || books.map(book => ({ type: 'spine', book }));
      
      return (
        <div 
          className="relative bg-white mx-auto print:border-none" 
          style={{ 
            width: '8.5in', 
            height: '11in',
            pageBreakAfter: 'always'
          }}
        >
          <div className="text-xs text-slate-600 p-2 print:hidden">
            Demco 1" × 1.5" - Spine Labels (Sheet {sheetNumber}) - 5 columns × 10 rows
            {getOversizedCount() > 0 && ` (includes ${getOversizedCount()} oversized stickers)`}
          </div>
          {items.map((item, index) => {
            const startPosition = isFirstSheet ? labelSettings.spineStartPosition : 1;
            const position = getLabelPosition(
              index, 
              startPosition, 
              5,
              1.65,
              1.0,
              0.45,
              0.28
            );
            
            if (item.type === 'oversized') {
              return (
                <OversizedLabel 
                  key={`oversized-${item.book.id}`}
                  style={{ 
                    left: `${position.left}in`, 
                    top: `${position.top}in` 
                  }} 
                />
              );
            } else {
              return (
                <SpineLabel 
                  key={item.book.id} 
                  book={item.book} 
                  style={{ 
                    left: `${position.left}in`, 
                    top: `${position.top}in` 
                  }} 
                />
              );
            }
          })}
        </div>
      );
    } else if (previewMode === 'oversized') {
      // Standalone oversized stickers - skip every other row to prevent clipping
      // 5 columns x 5 rows (every other row) = 25 labels per sheet
      const count = labelItems?.length || standaloneOversizedCount;
      const items = Array.from({ length: count }, (_, i) => ({ type: 'oversized', index: i }));
      
      return (
        <div 
          className="relative bg-white mx-auto print:border-none" 
          style={{ 
            width: '8.5in', 
            height: '11in',
            pageBreakAfter: 'always'
          }}
        >
          <div className="text-xs text-slate-600 p-2 print:hidden">
            Demco 1" × 1.5" - Oversized Stickers (Sheet {sheetNumber}) - 5 columns × 5 rows (alternating)
          </div>
          {items.map((item, index) => {
            const startPosition = isFirstSheet ? labelSettings.oversizedStartPosition : 1;
            // Double the row height (2.0 instead of 1.0) to skip every other row
            const position = getLabelPosition(
              index, 
              startPosition, 
              5,
              1.65,
              2.0,  // Double height = skip rows
              0.45,
              0.28
            );
            
            return (
              <OversizedLabel 
                key={`standalone-oversized-${index}`}
                style={{ 
                  left: `${position.left}in`, 
                  top: `${position.top}in` 
                }} 
              />
            );
          })}
        </div>
      );
    }
  };

  // Multi-Sheet Preview Component
  const MultiSheetPreview = () => {
    const selectedBooksData = getSelectedBooksData();
    const sheets = [];
    
    if (previewMode === 'barcode') {
      const labelsPerSheet = 30;
      const startPosition = labelSettings.barcodeStartPosition;
      const firstSheetCapacity = labelsPerSheet - startPosition + 1;
      
      // First sheet with custom start position
      const firstSheetBooks = selectedBooksData.slice(0, firstSheetCapacity);
      if (firstSheetBooks.length > 0) {
        sheets.push(
          <PreviewSheet 
            key={0}
            books={firstSheetBooks}
            sheetNumber={1}
            isFirstSheet={true}
          />
        );
      }
      
      // Remaining sheets starting at position 1
      let remainingBooks = selectedBooksData.slice(firstSheetCapacity);
      let sheetNumber = 2;
      
      while (remainingBooks.length > 0) {
        const sheetBooks = remainingBooks.slice(0, labelsPerSheet);
        sheets.push(
          <PreviewSheet 
            key={sheetNumber - 1}
            books={sheetBooks}
            sheetNumber={sheetNumber}
            isFirstSheet={false}
          />
        );
        remainingBooks = remainingBooks.slice(labelsPerSheet);
        sheetNumber++;
      }
    } else if (previewMode === 'spine') {
      const labelsPerSheet = 50;
      const startPosition = labelSettings.spineStartPosition;
      
      // Build array of label items: each book gets a spine label, oversized books also get an oversized sticker
      const allLabelItems = [];
      selectedBooksData.forEach(book => {
        // Always add spine label first
        allLabelItems.push({ type: 'spine', book });
        // If oversized, add oversized sticker immediately after
        if (book.isOversized) {
          allLabelItems.push({ type: 'oversized', book });
        }
      });
      
      const firstSheetCapacity = labelsPerSheet - startPosition + 1;
      
      // First sheet with custom start position
      const firstSheetItems = allLabelItems.slice(0, firstSheetCapacity);
      if (firstSheetItems.length > 0) {
        sheets.push(
          <PreviewSheet 
            key={0}
            books={[]}
            labelItems={firstSheetItems}
            sheetNumber={1}
            isFirstSheet={true}
          />
        );
      }
      
      // Remaining sheets starting at position 1
      let remainingItems = allLabelItems.slice(firstSheetCapacity);
      let sheetNumber = 2;
      
      while (remainingItems.length > 0) {
        const sheetItems = remainingItems.slice(0, labelsPerSheet);
        sheets.push(
          <PreviewSheet 
            key={sheetNumber - 1}
            books={[]}
            labelItems={sheetItems}
            sheetNumber={sheetNumber}
            isFirstSheet={false}
          />
        );
        remainingItems = remainingItems.slice(labelsPerSheet);
        sheetNumber++;
      }
    } else if (previewMode === 'oversized') {
      // Standalone oversized stickers - 25 per sheet (alternating rows)
      const labelsPerSheet = 25;
      const startPosition = labelSettings.oversizedStartPosition;
      const firstSheetCapacity = labelsPerSheet - startPosition + 1;
      
      let remaining = standaloneOversizedCount;
      let sheetNumber = 1;
      
      // First sheet
      const firstSheetCount = Math.min(remaining, firstSheetCapacity);
      if (firstSheetCount > 0) {
        sheets.push(
          <PreviewSheet 
            key={0}
            books={[]}
            labelItems={Array.from({ length: firstSheetCount }, (_, i) => ({ type: 'oversized', index: i }))}
            sheetNumber={1}
            isFirstSheet={true}
          />
        );
        remaining -= firstSheetCount;
        sheetNumber++;
      }
      
      // Remaining sheets
      while (remaining > 0) {
        const sheetCount = Math.min(remaining, labelsPerSheet);
        sheets.push(
          <PreviewSheet 
            key={sheetNumber - 1}
            books={[]}
            labelItems={Array.from({ length: sheetCount }, (_, i) => ({ type: 'oversized', index: i }))}
            sheetNumber={sheetNumber}
            isFirstSheet={false}
          />
        );
        remaining -= sheetCount;
        sheetNumber++;
      }
    }
    
    return <>{sheets}</>;
  };

  const handlePrint = () => {
    // Set custom document title for download filename
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    let labelType = 'Barcodes';
    if (previewMode === 'spine') labelType = 'Spine-Labels';
    else if (previewMode === 'oversized') labelType = 'Oversized-Stickers';
    
    const originalTitle = document.title;
    document.title = `HEIMDL-${labelType}-${dateStr}`;
    
    const printStyles = `
      @media print {
        @page { 
          size: 8.5in 11in; 
          margin: 0; 
        }
        body { 
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .print\\:hidden { 
          display: none !important; 
        }
        .print\\:border-none { 
          border: none !important; 
        }
        .print\\:shadow-none { 
          box-shadow: none !important; 
        }
      }
    `;
    
    const styleSheet = document.createElement('style');
    styleSheet.textContent = printStyles;
    document.head.appendChild(styleSheet);
    
    window.print();
    
    setTimeout(() => {
      document.head.removeChild(styleSheet);
      document.title = originalTitle;
    }, 1000);
  };

  const markBooksAsPrinted = async () => {
    const selectedBooksData = getSelectedBooksData();
    const batch = writeBatch(db);
    
    selectedBooksData.forEach(book => {
      const bookRef = doc(db, 'users', currentUser.uid, 'books', book.id);
      batch.update(bookRef, { 
        status: 'printed',
        printedAt: new Date()
      });
    });
    
    await batch.commit();
    alert(`Marked ${selectedBooksData.length} books as printed!`);
    loadBooksForPrinting();
  };

  const generateAddCopiesSheetHTML = () => {
    const selectedBooksData = getSelectedBooksData();

    // Group by call number and title
    const groupedByTitle = selectedBooksData.reduce((acc, book) => {
      const key = `${book.callNumber}|${book.title}`;
      if (!acc[key]) {
        acc[key] = {
          callNumber: book.callNumber,
          title: book.title,
          barcodes: []
        };
      }
      acc[key].barcodes.push(book.barcode);
      return acc;
    }, {});

    const groups = Object.values(groupedByTitle);
    const today = new Date().toLocaleDateString();

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Add Copies Sheet - ${today}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            h1 {
              color: #4f46e5;
              border-bottom: 3px solid #4f46e5;
              padding-bottom: 10px;
            }
            .date {
              color: #666;
              font-size: 14px;
              margin-bottom: 30px;
            }
            .book-group {
              margin-bottom: 25px;
              padding: 15px;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              background: #f9fafb;
            }
            .call-number {
              font-family: 'Courier New', monospace;
              font-weight: bold;
              color: #7c3aed;
              font-size: 16px;
            }
            .title {
              font-weight: bold;
              color: #1f2937;
              margin: 5px 0;
            }
            .barcodes {
              font-family: 'Courier New', monospace;
              color: #4b5563;
              font-size: 14px;
              margin-top: 5px;
            }
            .summary {
              margin-top: 30px;
              padding: 20px;
              background: #eff6ff;
              border: 2px solid #3b82f6;
              border-radius: 8px;
            }
            @media print {
              body {
                padding: 20px;
              }
              .book-group {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <h1>📚 Add Copies to Catalog</h1>
          <div class="date">Generated: ${today}</div>

          <p><strong>Instructions:</strong> Add the following copies to their respective titles in your MARC catalog. Each book listed below needs additional copy records with the barcodes shown.</p>

          ${groups.map(group => `
            <div class="book-group">
              <div class="call-number">${group.callNumber}</div>
              <div class="title">${group.title}</div>
              <div class="barcodes">
                Add ${group.barcodes.length} ${group.barcodes.length === 1 ? 'copy' : 'copies'}: ${group.barcodes.join(', ')}
              </div>
            </div>
          `).join('')}

          <div class="summary">
            <strong>Summary:</strong><br>
            Total Books: ${groups.length}<br>
            Total Copies: ${selectedBooksData.length}<br>
            Barcode Range: ${selectedBooksData[0]?.barcode} - ${selectedBooksData[selectedBooksData.length - 1]?.barcode}
          </div>
        </body>
      </html>
    `;
  };

  const handlePrintBookList = () => {
    const selectedBooksData = getSelectedBooksData();

    if (selectedBooksData.length === 0) {
      alert('No books selected to print');
      return;
    }

    // Open a new window with printable HTML
    const printWindow = window.open('', '_blank');
    const html = generateAddCopiesSheetHTML();
    printWindow.document.write(html);
    printWindow.document.close();

    // Trigger print dialog
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading books for printing...</div>
      </div>
    );
  }

  const numSheets = calculateSheets();
  const oversizedCount = getOversizedCount();

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50 print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-purple-300">|</span>
              <span className="hidden sm:inline text-purple-200">The Rune Printer</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right">
                <p className="text-sm text-purple-300">Books Found</p>
                <p className="text-xl font-bold">{books.length}</p>
              </div>
              {oversizedCount > 0 && (
                <div className="text-right">
                  <p className="text-sm text-orange-300">Oversized</p>
                  <p className="text-xl font-bold text-orange-200">{oversizedCount}</p>
                </div>
              )}
              <div className="text-right">
                <p className="text-sm text-purple-300">Library</p>
                <p className="text-lg font-bold">{settings?.libraryName || settings?.schoolName || 'Loading...'}</p>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Debug/Error Info */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 mx-4 mt-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Controls */}
      <div className="print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300 p-6 mb-6">
            <h1 className="text-2xl font-bold mb-4 text-slate-800">📜 The Rune Printer</h1>
            <p className="text-slate-600 mb-6">Transform your cataloged books into printable labels</p>
            
            {/* Navigation Links */}
            <div className="mb-6 flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/forge')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                ⚒️ Back to Forge
              </button>
              <button
                onClick={() => navigate('/bifrost')}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm"
              >
                🌈 Export MARC
              </button>
            </div>
            
            {/* Library Name Display */}
            {settings && (
              <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-lg p-4 mb-6 border border-blue-200">
                <h3 className="font-bold text-blue-800 mb-1">Library Information</h3>
                <p className="text-blue-700">
                  Barcode labels will display: <strong>{settings.libraryName || settings.schoolName}</strong>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Change this in Yggdrasil Settings if needed
                </p>
              </div>
            )}
            
            {books.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-600">No books ready for printing.</p>
                <button 
                  onClick={() => navigate('/forge')}
                  className="mt-4 bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
                >
                  Visit the Forge
                </button>
              </div>
            ) : (
              <>
                {/* Label Type Toggle - Now with 3 options */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <button
                    onClick={() => setPreviewMode('barcode')}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      previewMode === 'barcode' 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-slate-300 text-slate-700 hover:bg-slate-250'
                    }`}
                  >
                    Barcode Labels (Avery 5160)
                  </button>
                  <button
                    onClick={() => setPreviewMode('spine')}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      previewMode === 'spine' 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-slate-300 text-slate-700 hover:bg-slate-250'
                    }`}
                  >
                    Spine Labels (Demco 1" x 1.5")
                    {oversizedCount > 0 && (
                      <span className="ml-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                        +{oversizedCount} oversized
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setPreviewMode('oversized')}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      previewMode === 'oversized' 
                        ? 'bg-orange-600 text-white' 
                        : 'bg-orange-200 text-orange-800 hover:bg-orange-300'
                    }`}
                  >
                    📐 Oversized Stickers Only
                  </button>
                </div>

                {/* Settings */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  {previewMode === 'barcode' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Start at position:
                      </label>
                      <select
                        value={labelSettings.barcodeStartPosition}
                        onChange={(e) => setLabelSettings({...labelSettings, barcodeStartPosition: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                      >
                        {Array.from({length: 30}, (_, i) => (
                          <option key={i + 1} value={i + 1}>Position {i + 1}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {previewMode === 'spine' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Start at position:
                      </label>
                      <select
                        value={labelSettings.spineStartPosition}
                        onChange={(e) => setLabelSettings({...labelSettings, spineStartPosition: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                      >
                        {Array.from({length: 50}, (_, i) => (
                          <option key={i + 1} value={i + 1}>Position {i + 1}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {previewMode === 'oversized' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Start at position:
                        </label>
                        <select
                          value={labelSettings.oversizedStartPosition}
                          onChange={(e) => setLabelSettings({...labelSettings, oversizedStartPosition: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                        >
                          {Array.from({length: 25}, (_, i) => (
                            <option key={i + 1} value={i + 1}>Position {i + 1}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Number of stickers:
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="200"
                          value={standaloneOversizedCount}
                          onChange={(e) => setStandaloneOversizedCount(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                        />
                      </div>
                    </>
                  )}
                  
                  {previewMode !== 'oversized' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Selected books:
                      </label>
                      <p className="text-lg font-bold text-purple-600">
                        {selectedBooks.length} of {books.length}
                      </p>
                      <p className="text-sm text-slate-600">
                        {numSheets} sheet{numSheets !== 1 ? 's' : ''} needed
                        {previewMode === 'spine' && oversizedCount > 0 && (
                          <span className="text-orange-600"> (incl. {oversizedCount} oversized stickers)</span>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {previewMode === 'oversized' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Sheets needed:
                      </label>
                      <p className="text-lg font-bold text-orange-600">
                        {numSheets} sheet{numSheets !== 1 ? 's' : ''}
                      </p>
                      <p className="text-sm text-slate-600">
                        {standaloneOversizedCount} sticker{standaloneOversizedCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}

                  <div className="flex items-end space-x-2">
                    <button
                      onClick={handlePrint}
                      className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-medium"
                    >
                      Print {numSheets} Sheet{numSheets !== 1 ? 's' : ''}
                    </button>

                    {selectedBooks.length > 0 && (
                      <button
                        onClick={handlePrintBookList}
                        className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 font-medium"
                        title="Print a simple list of books for catalog updates"
                      >
                        📋 Print Book List
                      </button>
                    )}
                  </div>

                  {previewMode !== 'oversized' && (
                    <div className="flex items-end">
                      <button
                        onClick={markBooksAsPrinted}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 font-medium"
                      >
                        Mark as Printed
                      </button>
                    </div>
                  )}
                </div>

                {/* Sheet Info Alert */}
                {previewMode !== 'oversized' && selectedBooks.length > 0 && (
                  <div className="bg-blue-100 border border-blue-400 text-blue-800 px-4 py-3 rounded-lg mb-4">
                    <strong>Ready to print!</strong> You have {selectedBooks.length} book{selectedBooks.length !== 1 ? 's' : ''} selected, 
                    which will print on {numSheets} sheet{numSheets !== 1 ? 's' : ''} of {previewMode === 'barcode' ? 'barcode' : 'spine'} labels.
                    {previewMode === 'spine' && oversizedCount > 0 && (
                      <span className="text-orange-700"> This includes {oversizedCount} additional OVERSIZED sticker{oversizedCount !== 1 ? 's' : ''} for oversized books.</span>
                    )}
                    {' '}Labels are sorted by barcode number.
                  </div>
                )}
                
                {previewMode === 'oversized' && (
                  <div className="bg-orange-100 border border-orange-400 text-orange-800 px-4 py-3 rounded-lg mb-4">
                    <strong>Standalone Oversized Stickers</strong> - Print a batch of {standaloneOversizedCount} OVERSIZED stickers 
                    for manual shelf location changes. Prints on alternating rows (25 per sheet) with "OVERSIZED" rotated vertically.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Book Selection - Only show for non-oversized modes */}
          {books.length > 0 && previewMode !== 'oversized' && (
            <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300 mb-6">
              <div className="px-6 py-4 border-b border-slate-300 flex justify-between items-center bg-gradient-to-r from-slate-300 to-slate-250 rounded-t-xl">
                <h2 className="text-xl font-bold text-slate-800">
                  Select Books for Printing ({books.length} total)
                </h2>
                <div className="space-x-2">
                  <button
                    onClick={() => setSelectedBooks(books.map(book => book.id))}
                    className="text-purple-600 hover:text-purple-500 text-sm font-medium"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedBooks([])}
                    className="text-red-600 hover:text-red-500 text-sm font-medium"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="px-6 py-3 border-b border-slate-300">
                <input
                  type="text"
                  placeholder="Search by title, author, barcode, call number..."
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="max-h-96 overflow-y-auto">
                {books.filter(book => {
                  if (!searchTerm) return true;
                  const term = searchTerm.toLowerCase();
                  return (
                    book.title?.toLowerCase().includes(term) ||
                    book.author?.toLowerCase().includes(term) ||
                    book.barcode?.includes(term) ||
                    book.callNumber?.toLowerCase().includes(term) ||
                    book.location?.toLowerCase().includes(term)
                  );
                }).map((book) => (
                  <div key={book.id} className="px-6 py-3 border-b border-slate-300 last:border-b-0 hover:bg-slate-150">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedBooks.includes(book.id)}
                        onChange={() => toggleBookSelection(book.id)}
                        className="h-4 w-4 text-purple-600"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">
                          {book.title}
                          {book.isOversized && (
                            <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                              OVERSIZED
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-600">
                          {book.author} • Barcode: {book.barcode} • {book.callNumber}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Print Preview - Only visible when printing */}
      {((previewMode !== 'oversized' && books.length > 0 && selectedBooks.length > 0) || 
        (previewMode === 'oversized' && standaloneOversizedCount > 0)) && (
        <div className="print:block hidden">
          <MultiSheetPreview />
        </div>
      )}

      {/* Scale Preview for screen */}
      {((previewMode !== 'oversized' && books.length > 0 && selectedBooks.length > 0) ||
        (previewMode === 'oversized' && standaloneOversizedCount > 0)) && (
        <div className="print:hidden max-w-4xl mx-auto px-4 pb-8">
          <h3 className="text-lg font-bold text-slate-800 mb-4">
            Preview (scaled for screen) - {numSheets} sheet{numSheets !== 1 ? 's' : ''} 
            {previewMode !== 'oversized' && ' - Sorted by barcode'}
          </h3>
          <div className="space-y-8">
            <div className="transform scale-50 origin-top border-2 border-purple-300 rounded-lg p-4 bg-slate-150">
              <MultiSheetPreview />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RunePrinter;